import axios from 'axios';
import {API_URL} from '../config/constants';
import SpringDataPage, {BasicPage} from '../@types/spring-data-page';
import {getErrorMessage} from '../utils/utils';
import OccurrenceReportDescriptor from '../@types/occurrence-report-descriptor';
import Group from '../@types/group';
import {makeGroup} from '../utils/group-utils';


class OccurrenceReportDescriptorService {
    static descriptorUrlV2 = API_URL + 'api/v2/descriptors';

    formOccurrenceReportDescriptor(descriptor: any): OccurrenceReportDescriptor {
        let ord: OccurrenceReportDescriptor = {} as OccurrenceReportDescriptor;
        ord.id = descriptor.id;
        ord.name = descriptor.name;
        ord.description = descriptor.description;
        ord.hasBeenPublished = descriptor.hasBeenPublished;
        ord.uri = descriptor.uri;
        ord.descriptorState = descriptor.descriptorState;
        ord.descriptorTerms = descriptor.descriptorTerms ? descriptor.descriptorTerms.map((term: any) => ({
            id: term.id,
            term: term.term,
            definition: term.definition,
            descriptorId: term.descriptorId
        })) : [];
        ord.group = descriptor.group ? makeGroup(descriptor.group) :  new Group();
        if (descriptor.version) {
            ord.version = descriptor.version;
        }

        return ord;
    }

    formOccurrenceReportDescriptorsPagePromise(occurrenceReportDescriptorsData: any): Promise<SpringDataPage<OccurrenceReportDescriptor>> {
        let descriptorsPage: SpringDataPage<OccurrenceReportDescriptor> = {} as SpringDataPage<OccurrenceReportDescriptor>;
        if (occurrenceReportDescriptorsData) {
            if (occurrenceReportDescriptorsData._embedded?.descriptorDTOList) {
                descriptorsPage.content = occurrenceReportDescriptorsData._embedded.descriptorDTOList?.map((descriptor: any) => this.formOccurrenceReportDescriptor(descriptor));
            } else {
                descriptorsPage.content = [];
            }
            descriptorsPage.page = occurrenceReportDescriptorsData.page;
        }

        return Promise.resolve(descriptorsPage);
    }

    fetchDescriptors(page: BasicPage, groupId?: number | null, searchTerm?: string, selectedDescriptorType?: number): Promise<SpringDataPage<OccurrenceReportDescriptor>> {
        if (selectedDescriptorType === 1) {
            return this.fetchDraftDescriptors(page, groupId, searchTerm);
        } else if (selectedDescriptorType === 2) {
            return this.fetchPublishedDescriptors(page, groupId, searchTerm);
        } else {
            return this.fetchAllDescriptors(page, groupId, searchTerm);
        }
    }
    
    fetchAllDescriptors(page: BasicPage, groupId?: number|null, searchTerm?: string): Promise<SpringDataPage<OccurrenceReportDescriptor>> {
        // const pageNumber = Math.floor(page.offset / page.size);
        // eslint-disable-next-line no-console
        // console.log('Fetching page:', page);
        let url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/all?`
            + `size=${page.size}&`
            + `page=${page.offset}&`
            + `sort=${page.sort},${page.direction}`;
        if (searchTerm && searchTerm !== '') url += `&searchTerm=${searchTerm}`;
        if (groupId) url += `&groupId=${groupId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return this.formOccurrenceReportDescriptorsPagePromise(response.data);
            })
            .catch(err => {
                throw new Error('Failed to get descriptors: ' + getErrorMessage(err));
            })
    }


    fetchDraftDescriptors(page: BasicPage, groupId?: number|null, searchTerm?: string): Promise<SpringDataPage<OccurrenceReportDescriptor>> {
        let url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/drafts?`
            + `size=${page.size}&`
            + `page=${page.offset}&`
            + `sort=${page.sort},${page.direction}`;
        if (searchTerm && searchTerm !== '') url += `&searchTerm=${searchTerm}`;
        if (groupId) url += `&groupId=${groupId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return this.formOccurrenceReportDescriptorsPagePromise(response.data);
            })
            .catch(err => {
                throw new Error('Failed to get descriptors: ' + getErrorMessage(err));
            })
    }

    fetchPublishedDescriptors(page: BasicPage, groupId?: number|null, searchTerm?: string): Promise<SpringDataPage<OccurrenceReportDescriptor>> {
        let url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/published?`
            + `size=${page.size}&`
            + `page=${page.offset}&`
            + `sort=${page.sort},${page.direction}`;
        if (searchTerm && searchTerm !== '') url += `&searchTerm=${searchTerm}`;
        if (groupId) url += `&groupId=${groupId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return this.formOccurrenceReportDescriptorsPagePromise(response.data);
            })
            .catch(err => {
                throw new Error('Failed to get descriptors: ' + getErrorMessage(err));
            })
    }

    fetchDescriptorById(descriptorId: number): Promise<OccurrenceReportDescriptor> {
        let url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/${descriptorId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return Promise.resolve(this.formOccurrenceReportDescriptor(response.data));
            })
            .catch(err => {
                throw new Error(`Failed to get descriptor ${descriptorId} ` + getErrorMessage(err));
            })
    }

    createDescriptor(descriptor: OccurrenceReportDescriptor): Promise<any> {
        return axios
            .post(`${OccurrenceReportDescriptorService.descriptorUrlV2}/${descriptor.group.id}`, descriptor, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            })
            .then(response => {
                return this.formOccurrenceReportDescriptor(response.data);
            })
            .catch(err => {
                throw new Error('Create descriptor failed with error: ' + getErrorMessage(err));
            })
    }

    putDescriptor(descriptor: OccurrenceReportDescriptor): Promise<any> {
        return axios
            .put(`${OccurrenceReportDescriptorService.descriptorUrlV2}/${descriptor.id}`, descriptor, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            })
            .then(response => {
                return this.formOccurrenceReportDescriptor(response.data);
            })
            .catch(err => {
                throw new Error('Create occurrence failed with error: ' + getErrorMessage(err));
            })
    }

    publishDescriptor(descriptorId: number): Promise<any> {
        return axios
            .post(`${OccurrenceReportDescriptorService.descriptorUrlV2}/publish/${descriptorId}`, {}, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            })
            .then(response => {
                return this.formOccurrenceReportDescriptor(response.data);
            })
            .catch(err => {
                throw new Error(`Publish descriptor ${descriptorId} failed with error: ` + getErrorMessage(err));
            })
    }

    deleteDescriptorById(id: number): Promise<void> {
        return axios
            .delete(OccurrenceReportDescriptorService.descriptorUrlV2 + '/' + id, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
            })
            .then(() => {
                return;
            })
            .catch(err => {
                throw new Error(`Delete descriptor ${id} failed with error: ` + getErrorMessage(err));
            })
    }

    testDescriptorMapping(descriptorId: number, request: any): Promise<any> {
        const url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/${descriptorId}/test-mapping`;
        return axios
            .post(url, request, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => response.data)
            .catch(err => {
                throw new Error('Descriptor mapping test failed: ' + getErrorMessage(err));
            });
    }

    /**
     * Generate deterministic correlation ID for descriptor-source combination
     */
    private generateCorrelationId(descriptorId: number, sources: string[]): string {
        const sortedSources = sources.sort().join('-');
        return `BULK-DESC-${descriptorId}-${sortedSources}`;
    }

    /**
     * Check for existing mapping job using correlation ID
     */
    async checkExistingMappingJob(descriptorId: number, sources: string[]): Promise<any | null> {
        const correlationId = this.generateCorrelationId(descriptorId, sources);
        
        try {
            const url = `${API_URL}api/v2/jobs/correlation/${correlationId}`;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                // No existing job found
                return null;
            }
            throw new Error('Failed to check existing mapping job: ' + getErrorMessage(err));
        }
    }

    /**
     * Submit bulk descriptor mapping job
     */
    async submitBulkDescriptorMapping(descriptorId: number, request: {
        sources: string[];
        confidenceThreshold: number;
    }): Promise<any> {
        const correlationId = this.generateCorrelationId(descriptorId, request.sources);

        const payload = {
            sourceNames: request.sources,
            confidenceThreshold: request.confidenceThreshold,
            correlationId
        };
        
        const url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/${descriptorId}/bulk-mapping`;
        return axios
            .post(url, payload, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => response.data)
            .catch(err => {
                throw new Error('Failed to submit bulk mapping job: ' + getErrorMessage(err));
            });
    }

    /**
     * Get job status by execution ID
     */
    async getJobStatus(executionId: number): Promise<any> {
        const url = `${API_URL}api/v2/jobs/${executionId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => response.data)
            .catch(err => {
                // Preserve 404 so callers can distinguish "job gone" from transient errors
                if (axios.isAxiosError(err) && err.response?.status === 404) {
                    throw new Error('Job execution not found: ' + executionId);
                }
                throw new Error('Failed to get job status: ' + getErrorMessage(err));
            });
    }

    /**
     * Cancel a running job by execution ID
     */
    async cancelJobExecution(executionId: number): Promise<void> {
        const url = `${API_URL}api/v2/jobs/${executionId}`;
        return axios
            .delete(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(() => undefined)
            .catch(err => {
                throw new Error('Failed to cancel job: ' + getErrorMessage(err));
            });
    }

    /**
     * Get job results
     */
    async getJobResults(executionId: number): Promise<any> {
        const url = `${API_URL}api/v2/jobs/${executionId}/result`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => response.data)
            .catch(err => {
                throw new Error('Failed to get job results: ' + getErrorMessage(err));
            });
    }

    /**
     * Get count of unmapped reports for a descriptor from specified sources.
     * Queries the DB directly so the result always reflects current state,
     * regardless of job history or cancellations.
     */
    async getMappingCoverage(descriptorId: number, sources: string[]): Promise<{ unmappedCount: number }> {
        const params = sources.map(s => `sourceNames=${encodeURIComponent(s)}`).join('&');
        const url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/${descriptorId}/bulk-mapping/coverage?${params}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => response.data as { unmappedCount: number })
            .catch(err => {
                throw new Error('Failed to get mapping coverage: ' + getErrorMessage(err));
            });
    }

    /**
     * Export all mapping data for a descriptor and given sources.
     * Returns descriptor metadata plus all mapped reports with their descriptor term associations.
     */
    async exportMappingData(descriptorId: number, source: string): Promise<any> {
        const url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/${descriptorId}/mapping-export?source=${encodeURIComponent(source)}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => response.data)
            .catch(err => {
                throw new Error('Failed to export mapping data: ' + getErrorMessage(err));
            });
    }

    /**
     * Import mapping data from an export file for a specific descriptor
     */
    async importMappingData(descriptorId: number, file: File): Promise<{ mapped: number; skipped: number; failed: number }> {
        const url = `${OccurrenceReportDescriptorService.descriptorUrlV2}/${descriptorId}/mapping-import`;
        const formData = new FormData();
        formData.append('file', file);
        return axios
            .post(url, formData, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' }
            })
            .then(response => response.data as { mapped: number; skipped: number; failed: number })
            .catch(err => {
                throw new Error('Failed to import mapping data: ' + getErrorMessage(err));
            });
    }

    /**
     * Delete bulk descriptor mappings for a specific source
     */
    async deleteBulkDescriptorMapping(descriptorId: number, sources: string[]): Promise<any> {
        const url = `${API_URL}api/v2/descriptors/${descriptorId}/bulk-mapping`;
        const correlationId = this.generateCorrelationId(descriptorId, sources);
        
        return axios
            .delete(url, {
                data: {
                    sourceNames: sources,
                    correlationId
                },
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => response.data)
            .catch(err => {
                throw new Error('Failed to delete bulk mapping: ' + getErrorMessage(err));
            });
    }
}

export default new OccurrenceReportDescriptorService();